/**
 * ConfirmModal — the app-standard confirmation dialog.
 *
 * Replaces the native `window.confirm()` (the ugly "an embedded page says…"
 * browser box) with the design-system `Modal`, so every destructive / important
 * confirm across screens looks and behaves the same. Controlled: the caller
 * owns `open` + the pending action.
 */
import type { ReactNode } from "react";
import { Modal, Btn } from "@v0y4ge/jrni-ui";

export interface ConfirmModalProps {
  open: boolean;
  title: ReactNode;
  /** Body copy (string or nodes). */
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
  /** Disables both buttons + blocks backdrop/escape close while an action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export default function ConfirmModal({
  open, title, message, confirmLabel, cancelLabel, danger, busy, onConfirm, onClose,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={440}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
    >
      {message !== undefined ? <Modal.Body>{message}</Modal.Body> : null}
      <Modal.Footer>
        <Btn variant="secondary" onClick={onClose} disabled={busy}>{cancelLabel ?? "Cancel"}</Btn>
        <Btn variant={danger ? "danger" : undefined} onClick={onConfirm} disabled={busy}>
          {busy ? "…" : (confirmLabel ?? "Confirm")}
        </Btn>
      </Modal.Footer>
    </Modal>
  );
}
